
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

/**
 * Hook for generating vector embeddings for database records
 * Used for semantic search capabilities
 */
export const useGenerateEmbeddings = () => {
  /**
   * Generate embeddings for a given text and store them with the specified record
   * @param text Text content to generate embeddings for
   * @param table Database table name where the record exists
   * @param recordId ID of the record to update with embeddings
   */
  const generateEmbeddings = async (
    text: string,
    table: string,
    recordId: string
  ): Promise<boolean> => {
    try {
      if (!text || text.trim() === "") {
        console.warn("Empty text provided for embeddings generation");
        return false;
      }

      //console.log(`Generating embeddings for ${table}:${recordId}`);
      const { error } = await supabase.functions.invoke("generate-embeddings", {
        body: {
          text,
          table,
          recordId,
        },
      });

      if (error) {
        throw new Error(`Error generating embeddings: ${error.message}`);
      }

      //console.log(`Successfully generated embeddings for ${table}:${recordId}`);
      return true;
    } catch (error) {
      console.error("Error generating embeddings:", error);
      toast({
        title: "Error",
        description: "Failed to generate search embeddings",
        variant: "destructive",
      });
      return false;
    }
  };

  /**
   * Generate embeddings for a candidate record
   * @param candidateId ID of the candidate
   * @param data Text content about the candidate (name, skills, etc.)
   */
  const generateCandidateEmbeddings = async (
    candidateId: string,
    data: string
  ): Promise<boolean> => {
    return generateEmbeddings(data, "candidates", candidateId);
  };

  /**
   * Generate embeddings for a job record
   * @param jobId ID of the job
   * @param data Text content about the job (title, description, etc.)
   */
  const generateJobEmbeddings = async (
    jobId: string,
    data: string
  ): Promise<boolean> => {
    return generateEmbeddings(data, "jobs", jobId);
  };

  /**
   * Generate embeddings for a company record
   * @param companyId ID of the company
   * @param data Text content about the company (name, description, etc.)
   */
  const generateCompanyEmbeddings = async (
    companyId: string,
    data: string
  ): Promise<boolean> => {
    return generateEmbeddings(data, "companies", companyId);
  };

  return {
    generateEmbeddings,
    generateCandidateEmbeddings,
    generateJobEmbeddings,
    generateCompanyEmbeddings,
  };
};
